import React, { useCallback } from 'react';
import { Button, Card, CardContent, Stack, Typography } from '@mui/material';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import type { Schema, SchemaEnrichmentSnapshotModel } from '../../services/api';
import { downloadBlob, toCsv } from '../../utils/downloadUtils';
import type { ReviewComment } from './types';

interface ApprovalExportsCardProps {
  draft: Schema;
  schemaSummary: Record<string, unknown> | null;
  enrichmentMetadata: Record<string, unknown>;
  schemaEnrichmentSnapshot: SchemaEnrichmentSnapshotModel | null;
  reviewComments: ReviewComment[];
}

const ApprovalExportsCard: React.FC<ApprovalExportsCardProps> = ({
  draft,
  schemaSummary,
  enrichmentMetadata,
  schemaEnrichmentSnapshot,
  reviewComments,
}) => {
  const handleExportApprovalJson = useCallback(() => {
    const packet = {
      generated_at: new Date().toISOString(),
      draft: {
        schema_id: draft.schema_id,
        project_id: draft.project_id,
        base_schema_id: draft.base_schema_id,
        status: draft.status,
        source_type: draft.source_type,
        created_at: draft.created_at,
        approved_at: draft.approved_at,
        metadata: draft.metadata ?? {},
      },
      schema_summary: schemaSummary,
      enrichment: enrichmentMetadata,
      schema_enrichment_snapshot: schemaEnrichmentSnapshot,
      review_comments: reviewComments,
    };
    downloadBlob(
      JSON.stringify(packet, null, 2),
      `schema-approval-${draft.schema_id}.json`,
      'application/json'
    );
  }, [draft, schemaSummary, enrichmentMetadata, schemaEnrichmentSnapshot, reviewComments]);

  const handleExportCommentsCsv = useCallback(() => {
    const rows: string[][] = [
      ['comment_id', 'author', 'created_at', 'message'],
      ...reviewComments.map((comment) => [
        comment.comment_id,
        comment.author,
        comment.created_at,
        comment.message.replace(/\r?\n/g, ' '),
      ]),
    ];
    downloadBlob(
      toCsv(rows),
      `schema-comments-${draft.schema_id}.csv`,
      'text/csv;charset=utf-8'
    );
  }, [draft, reviewComments]);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Approval Exports
        </Typography>
        <Stack spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<SaveAltIcon />}
            onClick={handleExportApprovalJson}
          >
            Download Approval Packet (JSON)
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveAltIcon />}
            onClick={handleExportCommentsCsv}
          >
            Download Reviewer Comments (CSV)
          </Button>
          <Typography variant="caption" color="text.secondary">
            Exports include the latest metadata, enrichment snapshot, diff summary, and reviewer
            commentary for the approval record.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ApprovalExportsCard;
